import classNames from 'classnames';
import './BulkRemoveAssetWidget.scss';
import { useEffect, useRef, useState } from 'react';
import { Common } from 'common/services/common.service';
import { CustomSwitch } from 'common/components/CustomSwitch/CustomSwitch';

interface BulkRemoveAssetWidgetProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (value: string, deleteAndBlacklist: boolean, onComplete: () => void) => void;
  subTab: string;
}

export function BulkRemoveAssetWidget(props: BulkRemoveAssetWidgetProps) {
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteAndBlacklist, setDeleteAndBlacklist] = useState<boolean>(true);
  const ref: any = useRef(null);
  const refTextArea = useRef<HTMLTextAreaElement>(null);

  const handleClickOutside = (event: Event) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      props.onClose();
      document.removeEventListener('click', handleClickOutside, true);
    }
  };

  useEffect(() => {
    if (props.visible && refTextArea.current) {
      refTextArea.current.value = '';
      refTextArea.current.focus();
    }
  }, [props.visible, refTextArea]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  const handleTextareaOnChange = () => {
    setError(false);
  };

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDeleteAndBlacklist(event.target.checked);
  };

  const validateInput = (value: string): boolean => {
    if (!value || value.trim().length <= 1) {
      return false;
    }

    const lines = value.split('\n').filter(line => line.trim().length > 0);

    if (props.subTab === 'key-domains') {
      for (let i = 0; i < lines.length; i++) {
        if (!Common.validateTldr(lines[i].trim())) {
          return false;
        }
      }
    } else if (props.subTab === 'related-names') {
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].trim().length <= 1) {
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = () => {
    if (!refTextArea.current || !validateInput(refTextArea.current.value)) {
      setError(true);
      return;
    }

    setLoading(true);

    const onComplete = () => {
      setLoading(false);
      if (refTextArea.current) {
        refTextArea.current.value = '';
      }
      props.onClose();
    };

    props.onSubmit(refTextArea.current.value, deleteAndBlacklist, onComplete);
  };

  const getPlaceholderText = () => {
    if (props.subTab === 'key-domains') {
      return 'Enter domains to remove (one per line)\nExample:\nexample.com\ntest.org';
    } else {
      return 'Enter names to remove (one per line)\nExample:\nCompany Name\nOrganization';
    }
  };

  const getTitle = () => {
    return props.subTab === 'key-domains' ? 'Remove Key Domains' : 'Remove Related Names';
  };

  return (
    <div ref={ref} className={classNames('BulkRemoveAssetWidget', { visible: props.visible })}>
      <h6>{getTitle()}</h6>
      <div className={classNames('input-wrapper', { error: error })}>
        <textarea
          ref={refTextArea}
          className="bulk-remove-asset-widget-textarea"
          placeholder={getPlaceholderText()}
          onChange={handleTextareaOnChange}
        />
      </div>

      {props.subTab === 'key-domains' && (
        <div className="toggle-wrapper">
          <div className="toggle-option">
            <span className="toggle-label">
              {deleteAndBlacklist ? 'Delete and Blacklist' : 'Delete and Retain'}
            </span>
            <CustomSwitch
              checked={deleteAndBlacklist}
              onChange={handleToggleChange}
            />
          </div>
          <div className="toggle-description">
            {deleteAndBlacklist
              ? 'Assets will be deleted and blacklisted'
              : 'Assets will be deleted and retained'
            }
          </div>
        </div>
      )}

      <div className="actions">
        <span onClick={props.onClose}>Cancel</span>
        <span
          className={classNames({ loading: loading, disabled: loading })}
          onClick={loading ? undefined : handleSubmit}
        >
          {loading ? 'Removing...' : 'Remove'}
        </span>
      </div>
    </div>
  );
}
